// <tri-dialog> — a native <dialog> with declarative openers. Markup
// convention (rendered by Components::Dialog; openers can live anywhere):
//
//   <button data-dialog-open="opportunities">View all</button>
//   …
//   <tri-dialog>
//     <dialog id="opportunities" class="…">
//       <header>… <button data-dialog-close>✕</button></header>
//       <div>…</div>
//     </dialog>
//   </tri-dialog>
//
// The element owns closing (✕ buttons, backdrop click; Escape is native).
// Opening is one delegated document listener, registered once per page
// load, that showModal()s whatever id the opener names — so openers work
// no matter where in the DOM they render. The dialog is closed before
// Turbo caches the page, since a cached snapshot can't restore the
// ::backdrop/top-layer state showModal() creates.

class TriDialog extends HTMLElement {
	#lifetime: AbortController | null = null;

	connectedCallback() {
		const dialog = this.querySelector<HTMLDialogElement>("dialog");
		if (!dialog) return;

		this.#lifetime?.abort();
		this.#lifetime = new AbortController();
		const { signal } = this.#lifetime;

		dialog.addEventListener(
			"click",
			(event) => {
				const target = event.target;
				if (!(target instanceof Element)) return;

				// Backdrop click: the <dialog> itself is the target only when the
				// click lands outside its children.
				if (target === dialog || target.closest("[data-dialog-close]")) {
					dialog.close();
				}
			},
			{ signal },
		);

		document.addEventListener("turbo:before-cache", () => dialog.close(), {
			signal,
		});
	}

	disconnectedCallback() {
		this.#lifetime?.abort();
	}
}

if (!customElements.get("tri-dialog")) {
	customElements.define("tri-dialog", TriDialog);

	// One delegated listener for openers anywhere in the page — guarded by
	// the define check above so Vite HMR re-evaluation can't stack copies.
	document.addEventListener("click", (event) => {
		const target = event.target;
		if (!(target instanceof Element)) return;

		const opener = target.closest<HTMLElement>("[data-dialog-open]");
		if (!opener) return;

		const dialog = document.getElementById(opener.dataset.dialogOpen ?? "");
		if (dialog instanceof HTMLDialogElement) {
			event.preventDefault();
			dialog.showModal();
		}
	});
}
